import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Star, Zap, ClipboardList, Compass, Medal, Sparkles, MessageCircle, Send, X, Bell, GraduationCap, Headphones, Target, Users, Home, Brain, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { LEVELS, CHARACTERS } from '../lib/levels';
import { useT } from '../lib/i18n';
import AppHeader from './AppHeader';
import AudiobookPlayer from './AudiobookPlayer';
import GoalsView from './GoalsView';
import SupportCrew from './SupportCrew';
import EffortCard from './EffortCard';
import Buddies from './Buddies';
import { useSubscription } from '../context/SubscriptionContext';
import { TrialBanner } from '../components/Paywall';

const getLevel = (points) => LEVELS.find((l) => points >= l.min && points <= l.max) || LEVELS[0];

const ONBOARDING_QS = [
  { key: 'goal',    q: "What's ONE big thing you want to be great at this year?" },
  { key: 'excites', q: 'What excites you most about learning?' },
  { key: 'hard',    q: "What's something that feels hard for you right now?" },
  { key: 'happy',   q: 'What makes you happiest?' },
  { key: 'admires', q: "Who's someone you admire, and why?" },
];

const OnboardingModal = ({ character, onDone }) => {
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);
  const cur = ONBOARDING_QS[step];
  const next = async () => {
    if (!(ans[cur.key] || '').trim()) { toast.error('Type a short answer'); return; }
    if (step < ONBOARDING_QS.length - 1) { setStep(step + 1); return; }
    setBusy(true);
    try { await api.post('/coach/onboarding', ans); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="coach-onboarding-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-squishyLg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-gold shrink-0">
            <img src={character.image} alt={character.name} className="w-full h-full object-cover bg-cream" />
          </div>
          <div>
            <div className="text-xs text-teal font-heading font-bold uppercase tracking-wider">Meet your coach</div>
            <div className="font-heading text-lg font-bold text-navy">{character.name} · Question {step + 1} of {ONBOARDING_QS.length}</div>
          </div>
        </div>
        <h3 className="font-heading text-xl font-bold text-navy mb-3">{cur.q}</h3>
        <textarea rows={3} value={ans[cur.key] || ''} onChange={(e) => setAns((a) => ({ ...a, [cur.key]: e.target.value }))}
          placeholder="Your answer…"
          className="w-full px-3 py-3 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body"
          data-testid="onboarding-answer" />
        <button onClick={next} disabled={busy}
          className="mt-4 w-full h-12 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60"
          data-testid="onboarding-next">
          {step < ONBOARDING_QS.length - 1 ? 'Next →' : (busy ? 'Saving…' : 'Finish & Meet Coach')}
        </button>
      </div>
    </div>
  );
};

const TUTOR_STORAGE_KEY = 'ouistupastu_last_tutor';

const TutorChat = ({ character, onClose }) => {
  const [sessionId] = useState(() => `sess-${Date.now()}`);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [tutors, setTutors] = useState([]);
  const [tutor, setTutor] = useState(() => {           // selected tutor object — persists across sessions
    try { const raw = localStorage.getItem(TUTOR_STORAGE_KEY); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });
  const [preview, setPreview] = useState(null);       // tutor being previewed (bio card)
  const [lastError, setLastError] = useState(null);   // {text} of message that failed to get a reply
  const scrollRef = useRef(null);
  useEffect(() => { api.get('/tutors').then(({ data }) => setTutors(data.tutors || [])).catch(() => {}); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);
  const selectTutor = (t) => {
    setTutor(t);
    setPreview(null);
    try { localStorage.setItem(TUTOR_STORAGE_KEY, JSON.stringify(t)); } catch {}
  };
  const postMessage = async (t) => {
    setLastError(null);
    setSending(true);
    try {
      const { data } = await api.post('/tutor/chat', { text: t, session_id: sessionId, tutor_id: tutor?.id });
      setMessages((m) => [...m, { id: data.id, role: 'tutor', text: data.text }]);
    } catch (err) {
      setLastError({ text: t });
      toast.error(err?.response?.data?.detail || 'Tutor is thinking… tap Retry.');
    } finally { setSending(false); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    const t = text.trim();
    setText('');
    setMessages((m) => [...m, { id: 't-' + Date.now(), role: 'user', text: t }]);
    await postMessage(t);
  };
  const retryLast = async () => {
    if (!lastError || sending) return;
    await postMessage(lastError.text);
  };
  const endSession = async () => {
    if (messages.length < 2) { onClose(); return; }
    setEnding(true);
    try {
      const { data } = await api.post(`/tutor/session/end?session_id=${encodeURIComponent(sessionId)}`);
      if (data.summary) toast.success('Session summary sent to your parent 📝');
    } catch {}
    finally { setEnding(false); onClose(); }
  };

  // --- Bio card view — sticky CTA always visible above the fold ---
  if (preview) return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="tutor-bio-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[92vh] flex flex-col shadow-squishyLg" onClick={(e) => e.stopPropagation()}>
        <div className="flex-1 overflow-y-auto">
          <div className="bg-gradient-to-br from-gold via-teal to-navy p-6 text-white relative">
            <button onClick={() => setPreview(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 flex items-center justify-center" data-testid="bio-back"><X className="w-4 h-4" /></button>
            <div className="w-24 h-24 rounded-full bg-white/20 border-4 border-gold mx-auto flex items-center justify-center text-5xl mb-3">{preview.kind === 'ouistupastu' ? '🐒' : preview.kind === 'african' ? '🌍' : preview.kind === 'philosopher' ? '🏛️' : preview.kind === 'computing' ? '💻' : preview.kind === 'explorer' ? '🧭' : preview.kind === 'writer' ? '✒️' : '🔬'}</div>
            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold" data-testid="bio-name">{preview.name}</h2>
              <div className="text-white/70 text-xs">{preview.years}</div>
              <div className="text-gold font-heading font-bold text-sm mt-1">{preview.headline}</div>
            </div>
          </div>
          <div className="p-5 pb-4 space-y-3">
            <div>
              <div className="text-[10px] font-heading font-bold text-navy/60 uppercase mb-1">Key achievements</div>
              <ul className="space-y-1">{preview.achievements.map((a, i) => <li key={i} className="text-navy text-sm">• {a}</li>)}</ul>
            </div>
            <div className="rounded-2xl bg-cream p-3 italic text-navy text-sm">"{preview.quote}"</div>
            <div className="rounded-2xl bg-teal/10 border-2 border-teal p-3">
              <div className="text-[10px] font-heading font-bold text-teal uppercase mb-0.5">Why learn with {preview.name.split(' ')[0]}</div>
              <div className="text-navy text-sm">{preview.why}</div>
            </div>
            <div className="rounded-2xl bg-gold/15 border-2 border-gold p-3">
              <div className="text-[10px] font-heading font-bold text-gold uppercase mb-0.5">Fun fact</div>
              <div className="text-navy text-sm">{preview.fun_fact}</div>
            </div>
          </div>
        </div>
        {/* Sticky footer CTA — always visible, above the app bottom nav */}
        <div className="shrink-0 p-3 bg-white border-t-2 border-cream shadow-[0_-4px_16px_rgba(15,76,76,0.08)]">
          <button onClick={() => selectTutor(preview)}
            style={{ backgroundColor: '#f4a623' }}
            className="w-full h-14 rounded-full text-white font-heading font-bold text-base hover:brightness-110 active:scale-[0.98] transition-all shadow-lg inline-flex items-center justify-center gap-2"
            data-testid="bio-start">
            Start Learning with {preview.name.split(' ')[0]} →
          </button>
        </div>
      </div>
    </div>
  );

  // --- Picker view ---
  if (!tutor) return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="tutor-picker-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-squishyLg" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-navy to-teal text-white p-4 flex items-center gap-3">
          <GraduationCap className="w-6 h-6 text-gold" />
          <div className="flex-1">
            <div className="font-heading font-bold">Choose your Socratic Tutor</div>
            <div className="text-xs text-white/75">Pick a coach — you can change any time</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-testid="picker-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 gap-2 bg-cream">
          {tutors.length === 0 && <div className="col-span-2 text-center py-8 text-navy/60 text-sm">Loading tutors…</div>}
          {tutors.map((t) => (
            <button key={t.id} onClick={() => setPreview(t)}
              className="text-left p-3 bg-white rounded-2xl border-2 border-cream hover:border-teal transition-colors"
              data-testid={`tutor-pick-${t.id}`}>
              <div className="text-2xl mb-1">{t.kind === 'ouistupastu' ? '🐒' : t.kind === 'african' ? '🌍' : t.kind === 'philosopher' ? '🏛️' : t.kind === 'computing' ? '💻' : t.kind === 'explorer' ? '🧭' : t.kind === 'writer' ? '✒️' : '🔬'}</div>
              <div className="font-heading font-bold text-navy text-sm leading-tight">{t.name}</div>
              <div className="text-navy/60 text-[10px] mt-0.5">{t.years}</div>
              <div className="text-teal text-[11px] mt-1 leading-tight line-clamp-2">{t.headline}</div>
            </button>
          ))}        </div>
      </div>
    </div>
  );

  // --- Chat view (tutor selected) — FULLSCREEN (whatsapp-style) ---
  const tutorEmoji = tutor && (tutor.kind === 'ouistupastu' ? '🐒' : tutor.kind === 'african' ? '🌍' : tutor.kind === 'philosopher' ? '🏛️' : tutor.kind === 'computing' ? '💻' : tutor.kind === 'explorer' ? '🧭' : tutor.kind === 'writer' ? '✒️' : '🔬');
  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="tutor-chat-modal">
      {/* Top bar: back arrow · avatar · name · Change Tutor */}
      <div className="bg-gradient-to-r from-navy to-teal text-white px-3 py-3 flex items-center gap-3 shadow-md shrink-0">
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors shrink-0" data-testid="tutor-back" aria-label="Back to dashboard">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-11 h-11 rounded-full bg-gold/20 border-2 border-gold shrink-0 flex items-center justify-center text-2xl">{tutorEmoji}</div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold truncate flex items-center gap-1"><GraduationCap className="w-4 h-4 text-gold" /> {tutor.name}</div>
          <div className="flex items-center gap-3 mt-0.5">
            <button onClick={() => { setTutor(null); try { localStorage.removeItem(TUTOR_STORAGE_KEY); } catch {} }}
              className="text-[11px] text-white/80 underline hover:text-white font-heading font-bold" data-testid="tutor-switch">Change Tutor</button>
            <button onClick={() => setPreview(tutor)}
              className="text-[11px] text-white/60 hover:text-white/90" data-testid="tutor-about">About this tutor</button>
          </div>
        </div>
        <button onClick={endSession} disabled={ending} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors shrink-0" data-testid="tutor-close" aria-label="End session">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable conversation area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-cream" data-testid="tutor-messages">
        {messages.length === 0 && (
          <div className="text-center py-8 space-y-2">
            <div className="text-5xl mb-2">{tutorEmoji}</div>
            <p className="text-navy font-heading font-bold">Hi! I'm {tutor.name}.</p>
            <p className="text-navy/70 text-sm font-heading">Ask me anything you're curious about.</p>
            <div className="mt-4 space-y-1.5">
              <p className="text-navy/50 text-xs italic">"What is gravity?"</p>
              <p className="text-navy/50 text-xs italic">"I don't get how to add fractions"</p>
              <p className="text-navy/50 text-xs italic">"Who lived in Ancient Egypt?"</p>
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl shadow-sm ${m.role === 'user' ? 'bg-navy text-white rounded-br-sm' : 'bg-white text-navy border border-slate-200 rounded-bl-sm'}`}>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white text-navy border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-teal animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-teal animate-bounce" style={{ animationDelay: '300ms' }} />
              <span className="ml-2 text-navy/60 text-xs">{tutor.name} is thinking deeply…</span>
            </div>
          </div>
        )}
        {lastError && !sending && (
          <div className="flex justify-start">
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl px-3.5 py-2.5 space-y-2 max-w-[85%]">
              <div className="text-navy text-sm">I'm thinking deeply about your question…</div>
              <button onClick={retryLast}
                className="inline-flex items-center gap-1.5 h-8 px-4 rounded-full bg-navy text-white text-xs font-heading font-bold hover:bg-navy/90"
                data-testid="tutor-retry">
                <RefreshCw className="w-3 h-3" /> Retry
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Fixed input bar */}
      <form onSubmit={send} className="p-3 pb-4 flex gap-2 border-t-2 border-cream bg-white shrink-0" data-testid="tutor-input-bar">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Ask ${tutor.name}…`}
          className="flex-1 h-12 px-4 rounded-full border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm bg-cream"
          data-testid="tutor-input" />
        <button type="submit" disabled={sending || !text.trim()}
          className="h-12 w-12 rounded-full bg-teal text-white flex items-center justify-center disabled:opacity-40 shadow-md hover:scale-105 active:scale-95 transition-transform"
          data-testid="tutor-send" aria-label="Send message">
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
};


const CoachChat = ({ character, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => { (async () => {
    try { const { data } = await api.get('/coach/messages'); setMessages(data.messages); } catch {}
  })(); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    const myText = text.trim();
    setText(''); setSending(true);
    setMessages((m) => [...m, { id: 'tmp-' + Date.now(), role: 'user', text: myText }]);
    try {
      const { data } = await api.post('/coach/chat', { text: myText });
      const { data: full } = await api.get('/coach/messages');
      setMessages(full.messages);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Chat failed'); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-navy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="coach-chat-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[90vh] flex flex-col shadow-squishyLg" onClick={(e) => e.stopPropagation()}>
        <div className="bg-navy text-white p-4 rounded-t-3xl flex items-center gap-3">
          <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-gold shrink-0"><img src={character.image} alt="" className="w-full h-full object-cover bg-cream" /></div>
          <div className="flex-1">
            <div className="font-heading font-bold">{character.name}</div>
            <div className="text-xs text-white/60">Your personal coach</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-testid="coach-close"><X className="w-4 h-4" /></button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-cream" data-testid="coach-messages">
          {messages.length === 0 && <p className="text-navy/50 text-sm text-center py-6">Say hi to {character.name} 👋</p>}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-2xl ${m.role === 'user' ? 'bg-navy text-white' : 'bg-white text-navy border-2 border-cream'}`}>
                <div className="text-sm whitespace-pre-wrap">{m.text}</div>
              </div>
            </div>
          ))}
          {sending && <div className="text-navy/40 text-xs italic pl-2">{character.name} is typing…</div>}
        </div>
        <form onSubmit={send} className="p-3 flex gap-2 border-t-2 border-cream">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask your coach…"
            className="flex-1 h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
            data-testid="coach-input" />
          <button type="submit" disabled={sending} className="h-11 w-11 rounded-xl bg-teal text-white flex items-center justify-center disabled:opacity-60" data-testid="coach-send"><Send className="w-4 h-4" /></button>
        </form>
      </div>
    </div>
  );
};

export default function ChildDashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const { status, requirePremium } = useSubscription();
  const [habits, setHabits] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [medals, setMedals] = useState([]);
  const [quests, setQuests] = useState([]);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [coachStatus, setCoachStatus] = useState({ onboarded: true });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showTutor, setShowTutor] = useState(false);
  const [tab, setTab] = useState('learn');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [h, a, m, q, cs, r] = await Promise.all([
          api.get('/habits/today'),
          api.get('/student/assignments').catch(() => ({ data: { assignments: [] } })),
          api.get('/student/medals').catch(() => ({ data: { medals: [] } })),
          api.get('/quests').catch(() => ({ data: { quests: [] } })),
          api.get('/coach/status').catch(() => ({ data: { onboarded: true } })),
          api.get('/coach/reminders').catch(() => ({ data: { reminders: [] } })),
        ]);
        setHabits(h.data.habits);
        setAssignments(a.data.assignments || []);
        setMedals(m.data.medals || []);
        setQuests(q.data.quests || []);
        setCoachStatus(cs.data);
        setReminders(r.data.reminders || []);
        if (cs.data.onboarded) {
          try { const { data } = await api.get('/coach/daily-challenge'); setDailyChallenge(data); } catch {}
        }
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    })();
  }, [user?.language]);

  const toggleHabit = async (habit) => {
    const nextVal = !habit.completed;
    setHabits((prev) => prev.map((h) => (h.key === habit.key ? { ...h, completed: nextVal } : h)));
    try {
      await api.post('/habits/toggle', { habit_key: habit.key, completed: nextVal });
      if (nextVal) toast.success(`${t('habits.nice_job')} ${habit.label} ✓`);
    } catch {
      setHabits((prev) => prev.map((h) => (h.key === habit.key ? { ...h, completed: !nextVal } : h)));
      toast.error(t('habits.error'));
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center font-heading text-navy text-xl">Loading…</div>;

  const points = user?.points || 0;
  const level = user?.level || getLevel(points);
  const isMaxLevel = level.name === 'Knowble' || level.next_at == null;
  const nextAt = level.next_at || level.max;
  const progress = isMaxLevel ? 100 : Math.min(100, ((points - level.min) / Math.max(1, nextAt - level.min)) * 100);
  const character = CHARACTERS[user?.character] || CHARACTERS.ouistiti;
  const levelName = t(`level.${level.name.toLowerCase()}`);

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader subtitle={t('header.dashboard_child')} />

      <main className="max-w-3xl mx-auto px-4 md:px-6 pt-6 pb-28 space-y-6" data-testid="child-dashboard">
        <TrialBanner />

        {/* --- HERO: Socratic Tutor CTA — most prominent feature --- */}
        {tab === 'learn' && (
          <button onClick={() => setShowTutor(true)}
            className="w-full bg-gradient-to-br from-gold via-teal to-navy rounded-3xl p-6 text-white text-left shadow-squishyLg border-4 border-gold hover:scale-[1.02] transition-transform"
            data-testid="hero-tutor-cta">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center shrink-0 animate-float">
                <Brain className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-navy text-gold font-heading font-bold text-[10px] uppercase tracking-wider">
                  <Sparkles className="w-2.5 h-2.5" /> Socratic Tutor
                </div>
                <h2 className="font-heading text-2xl font-bold leading-tight mt-1">Ask the Socratic Tutor</h2>
                <p className="text-white/90 text-sm mt-0.5">Any subject. Any question. I guide — you discover.</p>
              </div>
            </div>
          </button>
        )}

        {/* Effort Score — CORE app philosophy: reward the daily effort, not just the outcome */}
        {tab === 'learn' && <EffortCard />}

        {/* Top tabs kept for accessibility (bottom nav is the primary) */}
        <div className="hidden md:flex gap-2 bg-white rounded-full p-1 shadow-squishy border-2 border-cream w-fit" data-testid="child-tabs">
          <button onClick={() => setTab('learn')}
            className={`px-5 py-2 rounded-full font-heading font-bold text-sm transition-colors duration-200 ${tab === 'learn' ? 'bg-navy text-white' : 'text-navy hover:bg-cream'}`}
            data-testid="child-tab-learn">Home</button>
          <button onClick={() => setShowTutor(true)}
            className={`px-5 py-2 rounded-full font-heading font-bold text-sm inline-flex items-center gap-1.5 text-navy hover:bg-cream`}
            data-testid="child-tab-tutor-top"><Brain className="w-4 h-4" /> Tutor</button>
          <button onClick={() => setTab('buddies')}
            className={`px-5 py-2 rounded-full font-heading font-bold text-sm inline-flex items-center gap-1.5 transition-colors duration-200 ${tab === 'buddies' ? 'bg-navy text-white' : 'text-navy hover:bg-cream'}`}
            data-testid="child-tab-buddies">
            <Users className="w-4 h-4" /> Buddies
          </button>
          <button onClick={() => setTab('listen')}
            className={`px-5 py-2 rounded-full font-heading font-bold text-sm inline-flex items-center gap-1.5 transition-colors duration-200 ${tab === 'listen' ? 'bg-navy text-white' : 'text-navy hover:bg-cream'}`}
            data-testid="child-tab-listen">
            <Headphones className="w-4 h-4" /> Audiobook
          </button>
          <button onClick={() => setTab('goals')}
            className={`px-5 py-2 rounded-full font-heading font-bold text-sm inline-flex items-center gap-1.5 transition-colors duration-200 ${tab === 'goals' ? 'bg-navy text-white' : 'text-navy hover:bg-cream'}`}
            data-testid="child-tab-goals">
            <Target className="w-4 h-4" /> Goals
          </button>
        </div>

        {tab === 'listen' && <AudiobookPlayer />}
        {tab === 'goals' && <GoalsView />}
        {tab === 'buddies' && <Buddies />}
        {tab === 'learn' && <>
        <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="level-badge-card">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-gold shrink-0">
              <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold text-navy font-heading font-bold text-sm" data-testid="level-name">
                  <Star className="w-3.5 h-3.5 fill-current" /> {levelName}
                </span>
                <span className="text-navy/60 text-sm font-semibold" data-testid="level-points">{points} {t('level.pts')}</span>
              </div>
              <div className="mt-3 h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-teal to-gold rounded-full transition-[width] duration-500" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-2 text-xs text-navy/60">
                {isMaxLevel ? t('level.max_hint') : `${Math.max(0, nextAt - points)} ${t('level.next_hint')}`}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-navy rounded-3xl p-6 shadow-squishy text-white" data-testid="daily-quiz-card">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-gold" />
            <span className="text-gold font-heading font-semibold text-sm uppercase tracking-wider">{t('quiz.section_kicker')}</span>
          </div>
          <h2 className="font-heading text-2xl font-bold mb-1">{t('quiz.headline')}</h2>
          <p className="text-white/70 text-sm mb-5">{t('quiz.subline')}</p>
          <Link to="/quiz"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-heading font-bold text-lg bg-gold text-navy shadow-gold hover:scale-105 active:scale-95 transition-transform duration-200"
            data-testid="start-quiz-button">{t('quiz.start')}</Link>
        </section>

        {/* AI Coach — daily challenge + chat entry */}
        <section className="bg-gradient-to-br from-teal to-navy rounded-3xl p-6 shadow-squishyLg text-white" data-testid="coach-card">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-gold shrink-0 animate-float bg-white">
              <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gold font-heading font-semibold text-xs uppercase tracking-wider">Coach {character.name}</div>
              {!coachStatus.onboarded ? (
                <>
                  <h3 className="font-heading text-lg font-bold mt-1">Let's get to know you.</h3>
                  <p className="text-white/80 text-sm mt-1">Answer 5 quick questions so I can coach you personally.</p>
                  <button onClick={() => setShowOnboarding(true)}
                    className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-navy font-heading font-bold text-sm shadow-gold hover:scale-105 transition-transform"
                    data-testid="start-onboarding-button">
                    <Sparkles className="w-4 h-4" /> Start onboarding
                  </button>
                </>
              ) : (
                <>
                  <h3 className="font-heading text-lg font-bold mt-1">Today's Challenge</h3>
                  <p className="text-white/90 text-sm mt-1 leading-snug" data-testid="daily-challenge-text">
                    {dailyChallenge?.text || 'Loading…'}
                  </p>
                  <button onClick={() => setShowChat(true)}
                    className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-navy font-heading font-bold text-sm hover:scale-105 transition-transform"
                    data-testid="open-coach-chat-button">
                    <MessageCircle className="w-4 h-4" /> Talk to {character.name}
                  </button>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Tutor — Socratic homework helper */}
        <section className="bg-gradient-to-br from-gold to-navy rounded-3xl p-6 shadow-squishyLg text-white" data-testid="tutor-card">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><GraduationCap className="w-7 h-7 text-gold" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-gold font-heading font-semibold text-xs uppercase tracking-wider">Socratic Tutor</div>
              <h3 className="font-heading text-lg font-bold mt-1">Ask your tutor anything about school</h3>
              <p className="text-white/85 text-sm mt-1 leading-snug">Math, science, history, geography, language… I'll guide you to the answer, not hand it to you.</p>
              <button onClick={() => setShowTutor(true)}
                className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-navy font-heading font-bold text-sm hover:scale-105 transition-transform"
                data-testid="open-tutor-button">
                <GraduationCap className="w-4 h-4" /> Start a Tutoring Session
              </button>
            </div>
          </div>
        </section>

        {/* Reminders */}
        {reminders.length > 0 && (
          <section className="bg-white rounded-3xl p-4 shadow-squishy border-2 border-gold space-y-2" data-testid="reminders-card">
            {reminders.map((r) => (
              <div key={r.key} className="flex items-center gap-2 text-navy" data-testid={`reminder-${r.key}`}>
                <Bell className="w-4 h-4 text-gold shrink-0" />
                <span className="text-sm font-semibold">{r.text}</span>
              </div>
            ))}
          </section>
        )}

        <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="habits-card">
          <h2 className="font-heading text-xl font-bold text-navy mb-1">{t('habits.today')}</h2>
          <p className="text-navy/60 text-sm mb-4">{t('habits.subtitle')}</p>
          <div className="space-y-3">
            {habits.map((h) => (
              <button key={h.key} onClick={() => toggleHabit(h)}
                className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-colors duration-200 ${h.completed ? 'border-teal bg-teal/5' : 'border-slate-200 hover:border-teal/50'}`}
                data-testid={`habit-toggle-${h.key}`}>
                {h.completed ? <CheckCircle2 className="w-6 h-6 text-teal shrink-0" /> : <Circle className="w-6 h-6 text-slate-300 shrink-0" />}
                <span className={`font-semibold text-base ${h.completed ? 'text-navy line-through opacity-60' : 'text-navy'}`}>{h.label}</span>
              </button>
            ))}
          </div>
        </section>

        <SupportCrew childName={user?.name} />
        </>}

        {/* Assignments from teacher */}
        {tab === 'learn' && assignments.length > 0 && (
          <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="assignments-card">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="w-5 h-5 text-teal" />
              <h2 className="font-heading text-xl font-bold text-navy">Assigned by Teacher</h2>
            </div>
            <div className="space-y-3">
              {assignments.map((a) => (
                <div key={a.id} className={`rounded-2xl border-2 p-4 ${a.completed ? 'border-teal bg-teal/10' : 'border-slate-200'}`} data-testid={`assignment-item-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-navy">{a.title}</div>
                      <div className="text-xs text-navy/60 mt-0.5">Assigned by <span className="font-bold">{a.teacher_name}</span> · {a.subject}</div>
                      {a.description && <div className="text-sm text-navy/70 mt-1">{a.description}</div>}
                    </div>
                    {a.completed && <span className="text-teal text-xs font-heading font-bold shrink-0">Done ✓</span>}
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs font-semibold text-navy/70 mb-1">
                      <span>{a.progress} / {a.count} correct</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-teal to-gold rounded-full transition-[width] duration-500" style={{ width: `${(a.progress / a.count) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === 'learn' && medals.length > 0 && (
          <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="medals-card">
            <div className="flex items-center gap-2 mb-4">
              <Medal className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-xl font-bold text-navy">Topic Medals</h2>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {medals.map((m) => (
                <div key={m.subject} className="text-center p-2 rounded-2xl bg-cream" data-testid={`medal-${m.subject}`}>
                  <div className="text-3xl">{m.current ? m.current.emoji : '🎯'}</div>
                  <div className="font-heading font-bold text-navy text-[11px] uppercase mt-1">{m.subject}</div>
                  <div className="text-[10px] text-navy/60">{m.correct} correct</div>
                  {m.next && <div className="text-[9px] text-navy/50 mt-0.5">Next: {m.next.min}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Quests */}
        {tab === 'learn' && quests.length > 0 && (
          <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="quests-card">
            <div className="flex items-center gap-2 mb-4">
              <Compass className="w-5 h-5 text-teal" />
              <h2 className="font-heading text-xl font-bold text-navy">Quests</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {quests.slice(0, 4).map((q) => (
                <div key={q.id} className={`rounded-2xl border-2 p-4 ${q.completed ? 'border-teal bg-teal/10' : 'border-cream bg-white'}`} data-testid={`quest-item-${q.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="text-2xl">{q.badge}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-navy text-sm">{q.name}</div>
                      <div className="text-[11px] text-navy/60 mt-0.5">{q.subject} · {q.progress}/{q.count}</div>
                    </div>
                  </div>
                  <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-teal to-gold rounded-full transition-[width] duration-500" style={{ width: `${(q.progress / q.count) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
      {showOnboarding && <OnboardingModal character={character} onDone={async () => {
        setShowOnboarding(false);
        setCoachStatus({ onboarded: true });
        toast.success(`${character.name} is ready to coach you!`);
        try { const { data } = await api.get('/coach/daily-challenge'); setDailyChallenge(data); } catch {}
      }} />}
      {showChat && <CoachChat character={character} onClose={() => setShowChat(false)} />}
      {showTutor && <TutorChat character={character} onClose={() => setShowTutor(false)} />}

      {/* --- STICKY BOTTOM NAV: Home | Buddies | Tutor | Goals | Audiobook --- */}
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t-4 border-gold shadow-squishyLg" data-testid="child-bottom-nav">
        <div className="max-w-3xl mx-auto grid grid-cols-5">
          <button onClick={() => setTab('learn')}
            className={`py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-heading font-bold uppercase tracking-wider transition-colors ${tab === 'learn' && !showTutor ? 'text-teal' : 'text-navy/60 hover:text-navy'}`}
            data-testid="nav-home">
            <Home className="w-5 h-5" /> Home
          </button>
          <button onClick={() => setTab('buddies')}
            className={`py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-heading font-bold uppercase tracking-wider transition-colors ${tab === 'buddies' ? 'text-teal' : 'text-navy/60 hover:text-navy'}`}
            data-testid="nav-buddies">
            <Users className="w-5 h-5" /> Buddies
          </button>
          <button onClick={() => setShowTutor(true)}
            className="py-2 flex flex-col items-center gap-0.5 text-[10px] font-heading font-bold uppercase tracking-wider text-white bg-gradient-to-br from-gold to-teal transition-colors"
            data-testid="nav-tutor">
            <div className="-mt-6 w-12 h-12 rounded-full bg-gradient-to-br from-gold to-teal border-4 border-white shadow-gold flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-navy">Tutor</span>
          </button>
          <button onClick={() => setTab('goals')}
            className={`py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-heading font-bold uppercase tracking-wider transition-colors ${tab === 'goals' ? 'text-teal' : 'text-navy/60 hover:text-navy'}`}
            data-testid="nav-goals">
            <Target className="w-5 h-5" /> Goals
          </button>
          <button onClick={() => setTab('listen')}
            className={`py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-heading font-bold uppercase tracking-wider transition-colors ${tab === 'listen' ? 'text-teal' : 'text-navy/60 hover:text-navy'}`}
            data-testid="nav-audiobook">
            <Headphones className="w-5 h-5" /> Audiobook
          </button>
        </div>
      </nav>
    </div>
  );
}
